import {
  Alert,
  Box,
  Button,
  Flex,
  FormGroup,
  notification,
  Typography,
  useForm,
} from '@ergolabs/ui-kit';
import React, { FC, useState } from 'react';

import { whitelistSignup } from '../../../../../../api/whitelistSignup/whitelistSignup';
import { sigUsdAsset } from '../../../../../../common/assets/sigUsdAsset';
import { AssetControlFormItem } from '../../../../../../common/components/AssetControl/AssetControl';
import { FormTitle } from '../../../../../../common/components/FormTitle/FormTitle';
import {
  OperationForm,
  OperationValidator,
} from '../../../../../../common/components/OperationForm/OperationForm';
import { Currency } from '../../../../../../common/models/Currency';
import { CommitmentFormModel } from '../../../../common/CommitmentForm/CommitmentForm';

const MINIMUM_STAKED_ERGOPAD_AMOUNT = 12_500;

export interface ErgopadWhiteListFormProps {
  readonly address: string;
  readonly totalErgopadStaked: number;
}

export const ErgopadWhitelistForm: FC<ErgopadWhiteListFormProps> = ({
  totalErgopadStaked,
  address,
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const form = useForm<CommitmentFormModel>({
    amount: undefined,
    asset: sigUsdAsset,
    address,
  });

  const stakedErgopadAmountValidator: OperationValidator<
    CommitmentFormModel
  > = () =>
    totalErgopadStaked >= MINIMUM_STAKED_ERGOPAD_AMOUNT
      ? undefined
      : 'Insufficient ergopad balance';

  const commitmentRangeValidator: OperationValidator<CommitmentFormModel> = (
    form,
  ) => {
    if (form.value.amount?.gt(new Currency(10_000_00n, sigUsdAsset))) {
      return 'Max commitment is 10k SigUSD';
    }
    return undefined;
  };

  const enterAmountValidator: OperationValidator<CommitmentFormModel> = (
    form,
  ) => (form.value.amount?.isPositive() ? undefined : 'Enter an amount');

  const submit = ({
    value: { amount, address },
  }: FormGroup<CommitmentFormModel>) => {
    if (amount && address) {
      setSubmitting(true);
      whitelistSignup('spectrum-staker-202301wl', address, amount).subscribe({
        next: () => {
          notification.success({ message: 'Your commitment was received' });
          setSubmitting(false);
        },
        error: () => {
          notification.error({
            message: 'Wallet already signed up for this event',
          });
          setSubmitting(false);
        },
      });
    }
  };

  return (
    <Box borderRadius="l" padding={4}>
      <OperationForm
        submitting={submitting}
        form={form}
        validators={[
          stakedErgopadAmountValidator,
          enterAmountValidator,
          commitmentRangeValidator,
        ]}
        onSubmit={submit}
        actionCaption="Submit"
      >
        <Flex col>
          <Flex.Item marginBottom={6}>
            <FormTitle>Ergopad stakers whitelist</FormTitle>
          </Flex.Item>
          <Flex.Item marginBottom={stakedErgopadAmountValidator(form) ? 2 : 6}>
            <Box padding={2} borderRadius="l" secondary>
              <Flex align="center" justify="space-between">
                <Typography.Body>Balance:</Typography.Body>
                <Typography.Body strong>
                  {totalErgopadStaked} staked ergopad
                </Typography.Body>
              </Flex>
            </Box>
          </Flex.Item>
          {stakedErgopadAmountValidator(form) && (
            <Flex.Item marginBottom={6}>
              <Alert
                showIcon
                type="warning"
                message={
                  <Typography.Body>
                    You have only {totalErgopadStaked} staked ergopad, but you
                    must stake at least 12,500 ergopad tokens at this address by
                    January 22nd at 17:00 UTC to be eligible for this round. See
                    how to stake ergopad{' '}
                    <Button
                      href="https://www.ergopad.io/staking"
                      target="_blank"
                      type="link"
                      style={{
                        padding: 0,
                        height: 'auto',
                        display: 'inline-block',
                      }}
                    >
                      here
                    </Button>
                  </Typography.Body>
                }
              />
            </Flex.Item>
          )}
          <Flex.Item marginBottom={2}>
            <Typography.Body>Commitment in SigUSD</Typography.Body>
          </Flex.Item>
          <Flex.Item>
            <AssetControlFormItem
              readonly="asset"
              amountName="amount"
              tokenName="asset"
            />
          </Flex.Item>
        </Flex>
      </OperationForm>
    </Box>
  );
};
